import React, { useCallback } from "react";
import { Formik, FormikHelpers, FormikProps } from "formik";
import {
  Form,
  SubmitButton,
  Input,
  InputNumber,
  Select,
  DatePicker,
} from "formik-antd";
import GridWrapper from "/app/src/components/generic/gridWrapper";
import HeroDivider from "/app/src/components/HeroUi/Divider";
import Box from "/app/src/components/generic/components/box";
import { CostCenter, iField } from "/app/src/models";
import { simpleSchemaBuilder } from "/app/src/helpers";
import { useQuery } from "@tanstack/react-query";
import { costCenterService } from "/app/src/services";
import { Moment } from "moment";
import { SearchFilter } from "/app/src/components/generic/components/searchFilter";
import DebouncedMaterialSelect from "./debouncedMaterialSelect";
import { useTranslation } from "react-i18next";

interface OrderLineType {
  id?: number;
  materialName: string;
  quantity: number;
  allocate?: string;
  costCenterId?: string;
  hostIdentification?: string;
  releaseDate?: string;
  qualification?: string;
  productionDate?: string;
  expiryDate?: string;
  serialNumber?: string;
  Info1?: string;
  Info2?: string;
  Info3?: string;
  Info4?: string;
  Info5?: string;
}

interface NewOrderLineProps {
  addLine: (line: OrderLineType) => void;
  orderLineFields: iField[];
}

interface FormValues {
  materialName: string;
  quantity: number;
  [key: string]: string | number; // Allows dynamic fields from orderFields
}

/**
 * Creates a new order line component.
 */
export default function NewOrderLine({
  addLine,
  orderLineFields,
}: NewOrderLineProps) {
  const { t } = useTranslation();

  // Generate dynamic initial values based on orderLineFields
  const initialValues = useCallback(() => {
    const values: FormValues = { materialName: "", quantity: 0 };
    orderLineFields.forEach((field) => {
      values[field.name] = "";
    });
    return values;
  }, [orderLineFields]);

  /**
   * Handles the form submission event.
   */
  const onSubmitHandler = useCallback(
    (values: FormValues, actions: FormikHelpers<FormValues>) => {
      // Map form values to OrderBuilderLine dynamically
      const orderLine: FormValues = {
        materialName: values.materialName,
        quantity: values.quantity,
      };
      orderLineFields.forEach((field) => {
        orderLine[field.name] = values[field.name];
      });
      // Call the addLine function with the new order line
      addLine(orderLine);
      // Reset the form after submission
      actions.resetForm();
    },
    [addLine, orderLineFields],
  );

  // Keep the original query for cost centers (no search needed)
  const { data: costCenters } = useQuery({
    queryKey: ["costCenters"],
    queryFn: () => {
      return costCenterService.getAll();
    },
    initialData: { cost_centers: [] },
    select: (data: { cost_centers: CostCenter[] }) => data.cost_centers,
    enabled:
      orderLineFields.find((field) => field.name === "costCenterId") !==
      undefined,
  });

  const mapCostCenters = useCallback((costCenter: CostCenter) => {
    return (
      <Select.Option key={costCenter.id} value={costCenter.id}>
        {costCenter.name}
      </Select.Option>
    );
  }, []);

  const createDynamicOrderLineFieldsForm = useCallback(
    (setFieldValue) => {
      // Dynamically create onChangeDatePicker function for each field
      const getOnChangeDatePicker = (fieldName: string) => (value: Moment) => {
        setFieldValue(fieldName, value);
      };

      /**
       * Get the display value for a given field name or return a default label.
       * @param {string} fieldName - The name of the field to find.
       * @param {string} defaultLabel - The default label if field not found.
       * @returns {any} The values of the field or the default label.
       */
      const getNameFieldValue = (fieldName: string, defaultLabel) => {
        const field = orderLineFields.find((f) => f.name === fieldName);
        return field ? field.values : defaultLabel;
      };

      return (
        <>
          <Form.Item
            name="materialName"
            hasFeedback={false}
            label={getNameFieldValue(
              "materialName",
              t("translation:material_name"),
            )}
          >
            <DebouncedMaterialSelect
              name="materialName"
              placeholder={t("translation:select_material")}
              setFieldValue={setFieldValue}
            />
          </Form.Item>
          <Form.Item
            name="quantity"
            hasFeedback={false}
            label={t("translation:quantity")}
          >
            <InputNumber
              style={{ width: "100%" }} // Ensure the input takes full width
              name="quantity"
              size="large"
            />
          </Form.Item>
          {orderLineFields
            .filter((field) => field.name !== "materialName")
            .map((field) => (
              <Form.Item
                key={field.name}
                hasFeedback={false}
                name={field.name}
                label={field.values}
              >
                {field.name === "expiryDate" ||
                field.name === "productionDate" ||
                field.name === "releaseDate" ? (
                  <DatePicker
                    name={field.name}
                    showTime
                    onChange={getOnChangeDatePicker(field.name)}
                    size="large"
                    style={{ width: "100%" }}
                  />
                ) : field.name === "costCenterId" ? (
                  <SearchFilter
                    name={field.name}
                    mapOptionsFn={mapCostCenters}
                    list={costCenters}
                  />
                ) : (
                  <Input name={field.name} size="large" />
                )}
              </Form.Item>
            ))}
        </>
      );
    },
    [costCenters, mapCostCenters, orderLineFields, t],
  );

  /**
   * Renders the new order line form component.
   */
  const newOrderLineForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isValid, setFieldValue }) => (
        <Form layout="vertical">
          <div className="flex justify-between items-center">
            <h2 className="text-[18px]">{t("translation:order_lines")}</h2>
            <SubmitButton type="primary" disabled={!dirty || !isValid}>
              {t("translation:add_order_line")}
            </SubmitButton>
          </div>
          <HeroDivider className="my-3" />
          <GridWrapper responsive={{ sm: 1, md: 1, lg: 3 }}>
            {createDynamicOrderLineFieldsForm(setFieldValue)}
          </GridWrapper>
        </Form>
      ),
      [createDynamicOrderLineFieldsForm, t],
    );

  return (
    <Box className="!mb-[1px]">
      <Formik
        component={newOrderLineForm}
        enableReinitialize
        validationSchema={simpleSchemaBuilder([
          { name: "materialName", type: "string", required: true },
          { name: "quantity", type: "number", required: true },
          ...orderLineFields
            .filter((field) => field.name !== "materialName")
            .map((field) => ({
              name: field.name,
              type: "string" as const, // Assuming all fields are strings
              required: field.required,
            })),
        ])}
        initialValues={initialValues()}
        onSubmit={onSubmitHandler}
      />
    </Box>
  );
}
